import { useCreateFieldModalParams } from '@/components/FormCreator/hooks/useCreateFieldModalParams';
import { EditorMediaManager } from '@/components/TemplateEditor/components/EditorMediaManager';
import { useCustomFormats } from '@/services/hooks/editor/useCustomFormats';
import { useModules } from '@/components/TemplateEditor/hooks/useModules';
import { useShowToasts } from '@/components/TemplateEditor/hooks/useShowToast';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { useUpselling } from '@/services/hooks/useUpselling';
import { isIntercomEnabled } from '@/services/Secuirty';
import { getAccountAdvancedOptions } from '@/store/account/actions';
import { getDialogStore, HideDialog, ShowDialog } from '@/store/dialog/actions';
import {
    getCheckoutCreatorStore,
    OpenUpsellingOverlay,
    SaveStart,
    SetIsTemplateOpen,
    SetTemplatePreview,
    TemplateChanged,
    TemplateFormDuplicated,
    TemplateSetMultiSignUpEditMessage,
    TemplateShowSaveErrors,
} from '@/store/checkoutCreator/actions';
import { getMediaManagerStore, ShowMediaManager } from '@/store/mediaManager/actions';
import { getEditorFilesStore, UploadEditorFile, UploadEditorFileSuccess } from '@/store/templateEditor/actions';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { ElasticTheme, EmojiPickerLocale, palette } from '@dev/ee-components';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { getStoredLanguage } from '@dev/ee-tools';
import {
    TemplateEditor as Editor,
    EditorBlockDuplicationDetails,
    EditorBlockEventType,
    EditorBlockSelectionDetails,
    EditorCreateSignUpFieldEventCallback,
    EditorExternalValidationError,
    EditorMultiTemplates,
    EditorSaveData,
    EditorSaveError,
    EditorTemplateStatus,
    FileType,
} from '@dev/template-editor';
import { Portal } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';
import { useBrandColorsInTemplateEditor } from '@/services/hooks/useBrandColorsInTemplateEditor';
import { EditorType } from '@/services/dictionaries/EditorType';
import { MultiTemplateId } from '../../constants';
import { useShowTemplateIsBeingSavedToast } from '../../hooks/useShowTemplateIsBeingSavedToast';
import { UpsellingOverlay } from '../UpsellingOverlay';
import { useMultiTemplatesWebsiteOptions } from '../../hooks/useMultiTemplatesWebsiteOptions';
import { useSpecialFields } from '../../hooks/useSpecialFields';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${ElasticTheme.zIndex.modal};
    background: ${palette.snow[1]};

    .MuiContainer-root {
        padding-top: 60px;
    }
`;

const headerOptions = [];

interface TemplateEditorModalProps {
    isGalleryCreator: boolean
}

// eslint-disable-next-line max-lines-per-function
export const TemplateEditorModal = ({ isGalleryCreator }: TemplateEditorModalProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { template } = useSelector(getCheckoutCreatorStore);
    const dialog = useSelector(getDialogStore);
    const mediaManager = useSelector(getMediaManagerStore);
    const files = useSelector(getEditorFilesStore);
    const specialFields = useSpecialFields();

    const { onModuleAdd, onModuleAddLocked, onModuleDelete, onModuleNameEdit } = useModules();
    const { showModulesLimitReachedToast } = useShowToasts();
    const [blocksValidationErrors, setBlocksValidationErrors] = useState<EditorExternalValidationError[]>();

    const selectedBlockIdRef = useRef<string | undefined>();

    const templateRef = useRef(template);
    const { showTemplateIsBeingSavedToast } = useShowTemplateIsBeingSavedToast();
    const { boot: bootIntercom, shutdown: shutdownIntercom } = useIntercom() || {};
    const [showImageTabOnly, setShowImageTabOnly] = useState(false);
    const [isLinkToFile, setIsLinkToFile] = useState(false);
    const [currentBlockId, setCurrentBlockId] = useState<string>();
    const [multiTemplates, setMultiTemplates] = useState<EditorMultiTemplates>(null);
    const advancedOptions = useSelector(getAccountAdvancedOptions);
    const hasContactFeatures = advancedOptions?.EnableContactFeatures;
    const { isUpsellingVisible } = useUpselling(AccessLevel.ViewCustomFields);
    const historyBackAction = useHistoryBackActionContext();
    const { pushBackCallback, popBackCallback } = historyBackAction || {};
    const { getCreateFieldModalProps } = useCreateFieldModalParams({ popBackCallback });
    const { brandColors, onBrandColorAdd, onBrandColorEdit, onBrandColorDelete } = useBrandColorsInTemplateEditor();

    const customFormats = useCustomFormats(EditorType.CheckoutCreator);
    const multiTemplatesWebsiteModeOptions = useMultiTemplatesWebsiteOptions({ isGalleryCreator, selectedBlockIdRef });

    useEffect(() => {
        templateRef.current = template;
    }, [template]);

    useEffect(() => {
        shutdownIntercom();
        pushBackCallback(onBreadcrumbAction);
        document.documentElement.style.overflow = 'auto';
        return () => {
            isIntercomEnabled() && bootIntercom();
            bootIntercom();
            document.documentElement.style.overflow = null;
        };
    }, []);

    useEffect(() => {
        if (
            !template?.loadedJson ||
            ![EditorTemplateStatus.LOADED, EditorTemplateStatus.SAVED, EditorTemplateStatus.SAVING, EditorTemplateStatus.SAVE_ERROR].includes(templateRef.current.status)
        ) {
            return;
        }

        setMultiTemplates({
            status: template?.status ?? EditorTemplateStatus.DEFAULT,
            templates: [{
                id: MultiTemplateId.PRODUCT,
                name: 'Product',
                content: template.loadedJson, 
            },{
                id: MultiTemplateId.PAYMENT,
                name: 'Payment',
                content: template.paymentLoadedJson
            },
            {
                id: MultiTemplateId.CONFIRMATION,
                name: 'Confirmation',
                content: template.confirmationLoadedJson 
            },
            ]
        });
    }, [template?.loadedJson, template?.status]);

    const onTemplateChanged = () => {
        blocksValidationErrors && setBlocksValidationErrors(null);
        dispatch(TemplateChanged());
    };
    const getBlocksValidationErrors = (): EditorExternalValidationError[] => template.forms
    ?.filter((form) => !form.isDeleted && form.isError)
    .map(
        ({ blockClientId, isFirstTimeError }): EditorExternalValidationError => ({
            blockClientId,
            invalidSidebarTabsIndexes: [2],
            disableSidebarTabOpen: isFirstTimeError === false,
        })
    );
    const onSave = (data: EditorSaveData, exitOnSave: boolean, errors?: EditorSaveError[]): void => {
        const blocksValidationErrors = getBlocksValidationErrors();
        setBlocksValidationErrors(blocksValidationErrors);
        dispatch<any>(SaveStart({ data, exitOnSave, errors }))
            .then(() => {
                if (!exitOnSave || errors) {
                    return;
                }
                popBackCallback();
            })
            .catch(() => {
                console.log('Checkout failed to save.');
            });
    };

    const onBreadcrumbAction = (): boolean => {
        if (
            templateRef.current.status === EditorTemplateStatus.SAVING ||
            templateRef.current.status === EditorTemplateStatus.MODIFIED
        ) {
            showTemplateIsBeingSavedToast();
            return false;
        }
        const blocksValidationErrors = getBlocksValidationErrors();
        setBlocksValidationErrors(blocksValidationErrors);
        if (blocksValidationErrors?.length || templateRef.current.status === EditorTemplateStatus.SAVE_ERROR) {
            dispatch(TemplateShowSaveErrors());
            return false;
        }
        dispatch(SetIsTemplateOpen(false));
        return true;
    };
    const onBreadcrumbClick = (): void => {
        onBreadcrumbAction() && popBackCallback();
    };

    const onPreviewTemplateOpen = (templateHTML: string) => {
        dispatch(SetTemplatePreview(templateHTML));
    };
    const closeModal = (): boolean => {
        dispatch(HideDialog());
        return true;
    };
    const onCreateSignUpField = (callback: EditorCreateSignUpFieldEventCallback) => {
        if (isUpsellingVisible) {
            dispatch(OpenUpsellingOverlay(AvailableFeaturesUpselling.CustomFields));
            return;
        }
        pushBackCallback(closeModal);
        dispatch(ShowDialog(getCreateFieldModalProps(callback)));
    };

    const openMediaManager = (blockId: string, isLinkToFile: boolean): void => {
        setCurrentBlockId(blockId);
        setShowImageTabOnly(!isLinkToFile);
        setIsLinkToFile(isLinkToFile);
        dispatch(ShowMediaManager());
    };

    const uploadFile = (file: File, blockId: string): void => {
        dispatch(UploadEditorFile(file, blockId));
    };

    const editorFilesUpdate = (files: FileType[]): void => {
        dispatch(UploadEditorFileSuccess(files));
    };

    const localeForEmoji = getStoredLanguage();

    const onBlockDuplication = (details: EditorBlockDuplicationDetails): void => {
        dispatch(TemplateFormDuplicated(details.block.clientId, details.clonedBlock.clientId));
    };

    const onMultiTemplateChange = (templateId: string): void => {
        console.log(templateId);
    }

    const onBlockSelection = (details: EditorBlockSelectionDetails): void => {
        if (template.multiSignUpEditMessage && (!details.block?.clientId || selectedBlockIdRef.current !== details.block?.clientId)) {
            dispatch(TemplateSetMultiSignUpEditMessage());
        }
        selectedBlockIdRef.current = details.block?.clientId;
    };

    const onBlockEvent = (eventType: EditorBlockEventType, details): void => {
        if (template.multiSignUpEditMessage && eventType === EditorBlockEventType.SIDEBAR_TAB_SWITCH) {
            dispatch(TemplateSetMultiSignUpEditMessage());
        }
    };

    if (!multiTemplates?.templates[0].content) {
        return null;
    }
    return (
        <Portal>
            <Overlay>
                <Editor
                    translations={
                        i18n.hasResourceBundle(i18n.language, 'template-editor')
                            ? i18n.getResourceBundle(i18n.language, 'template-editor')
                            : null
                    }
                    events={{
                        onFileUpload: uploadFile,
                        onMediaManagerOpen: openMediaManager,
                        onEditorFilesUpdate: editorFilesUpdate,
                        onTemplateChanged,
                        onSave,
                        onTemplatePreview: onPreviewTemplateOpen,
                        onBreadcrumbClick,
                        onBrandColorAdd,
                        onBrandColorDelete,
                        onBrandColorEdit,
                        onCreateSignUpField: hasContactFeatures ? onCreateSignUpField : null,
                        onModuleAdd,
                        onModuleDelete,
                        onModuleNameEdit,
                        onModuleLimitReached: showModulesLimitReachedToast,
                        onModuleAddLocked,
                        onBlockDuplication,
                        onBlockSelection,
                        onMultiTemplateChange,
                    }}
                    media={{
                        mediaManagerComponent: {
                            content: (
                                <EditorMediaManager
                                    showImageTabOnly={showImageTabOnly}
                                    isLinkToFile={isLinkToFile}
                                    blockId={currentBlockId}
                                    isOpen={mediaManager.isOpened}
                                />
                            ),
                            isMediaManagerOpen: mediaManager.isOpened,
                        },
                        files,
                    }}
                    autosaveOptions={{
                        counter: template?.autosaveCounter,
                    }}
                    navigation={{
                        breadcrumbText: t('backtocheckout'),
                        isContinueButtonVariant: true,
                    }}
                    colorPickerOptions={{
                        recentColors: template?.recentColors,
                        brandColors,
                    }}
                    emojiOptions={{
                        locale: localeForEmoji as EmojiPickerLocale,
                    }}
                    customFormats={customFormats}
                    multiTemplatesWebsiteModeOptions={multiTemplatesWebsiteModeOptions}
                    
                    headerOptions={headerOptions}
                    displayOptions={{
                        isBelowOtherLayer: dialog.isOpened,
                        popupsMinimalZIndex: 1400,
                    }}
                    blocksValidationErrors={blocksValidationErrors}
                    multiTemplates={multiTemplates}
                    specialFieldsOptions={specialFields}
                />
            </Overlay>
            <UpsellingOverlay />
        </Portal>
    );
};
